import { X, Printer, AlertTriangle, IndianRupee, Pill } from "lucide-react";
import { Button } from "../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { STATUS_CONFIG, formatCurrency, formatDateIST, formatTimeIST, formatMode } from "./pharmacyConstants";

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || { label: String(status), color: "text-slate-500", bg: "bg-slate-50", border: "border-slate-200", dot: "bg-slate-400" };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${cfg.bg} ${cfg.color} border ${cfg.border}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} /> {cfg.label}
    </span>
  );
}

export function PharmacyDrillDown({ drillOrder, onClose, handleCreditBill }) {
  if (!drillOrder) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="drill-down-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50">
          <div>
            <h3 className="text-sm font-bold text-slate-800">Order: {drillOrder.orderId}</h3>
            <p className="text-xs text-slate-500 mt-0.5">{drillOrder.patientName} | MRN: {drillOrder.mrn} | {drillOrder.patientType}</p>
          </div>
          <div className="flex items-center gap-3">
            <StatusBadge status={drillOrder.status} />
            {drillOrder.paymentModes && drillOrder.paymentModes.includes("CREDIT") && (
              <Button variant="outline" size="sm" onClick={() => handleCreditBill(drillOrder.orderId)} data-testid="credit-bill-modal-btn" className="text-xs gap-1 text-teal-700 border-teal-300 hover:bg-teal-50">
                <Printer className="w-3.5 h-3.5" /> Credit Bill
              </Button>
            )}
            <Button variant="ghost" size="icon" onClick={onClose} data-testid="close-drill"><X className="w-4 h-4" /></Button>
          </div>
        </div>
        <div className="overflow-y-auto max-h-[calc(85vh-120px)] p-6 space-y-5">
          {drillOrder.status === 301 || drillOrder.status === 302 ? (
            <div className={`rounded-lg p-3 ${STATUS_CONFIG[drillOrder.status]?.bg} border ${STATUS_CONFIG[drillOrder.status]?.border}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-slate-600">Payment Status: {drillOrder.statusLabel}</p>
                  {drillOrder.status === 302 && <p className="text-xs text-slate-500 mt-1">Paid: {formatCurrency(drillOrder.paidAmount || 0)} | Balance: {formatCurrency(drillOrder.balance)}</p>}
                  {drillOrder.status === 301 && <p className="text-xs text-slate-500 mt-1">Full Amount Pending: {formatCurrency(drillOrder.grandTotal)}</p>}
                </div>
                <AlertTriangle className="w-5 h-5 text-amber-500" />
              </div>
            </div>
          ) : null}

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              { label: "Subtotal", value: formatCurrency(drillOrder.totalAmount) },
              { label: "Discount", value: formatCurrency((drillOrder.totalDiscount || 0) + (drillOrder.additionalDiscount || 0)) },
              { label: "Tax", value: formatCurrency(drillOrder.totalTax) },
              { label: "Grand Total", value: formatCurrency(drillOrder.grandTotal), bold: true },
              { label: "Paid Amount", value: formatCurrency(drillOrder.paidAmount || 0), paid: true },
            ].map((item, i) => (
              <div key={i} className={`rounded-lg p-3 ${item.bold ? "bg-teal-50 border border-teal-200" : item.paid ? "bg-emerald-50 border border-emerald-200" : "bg-slate-50"}`}>
                <p className="text-[10px] text-slate-500 uppercase font-semibold">{item.label}</p>
                <p className={`text-base font-bold ${item.bold ? "text-teal-700" : item.paid ? "text-emerald-700" : "text-slate-700"}`}>{item.value}</p>
              </div>
            ))}
          </div>

          {drillOrder.paymentTransactions && drillOrder.paymentTransactions.length > 0 && (
            <div data-testid="drill-payment-info">
              <h4 className="text-xs font-bold text-slate-700 mb-2 flex items-center gap-1"><IndianRupee className="w-3.5 h-3.5" /> Payment Transactions ({drillOrder.paymentTransactions.length})</h4>
              <div className="rounded-lg border border-slate-200 overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="text-[10px] font-bold uppercase">Mode</TableHead>
                      <TableHead className="text-[10px] font-bold uppercase text-right">Amount</TableHead>
                      <TableHead className="text-[10px] font-bold uppercase">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {drillOrder.paymentTransactions.map((t, i) => (
                      <TableRow key={i}>
                        <TableCell className="text-xs font-medium">{formatMode(t.mode)}</TableCell>
                        <TableCell className="text-xs text-right tabular-nums font-semibold">{formatCurrency(t.amount)}</TableCell>
                        <TableCell className="text-xs">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${t.status === "PAID" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{t.status}</span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          <div>
            <h4 className="text-xs font-bold text-slate-700 mb-2 flex items-center gap-1"><Pill className="w-3.5 h-3.5" /> Medicines ({drillOrder.medicines?.length || 0})</h4>
            <div className="rounded-lg border border-slate-200 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-[10px] font-bold uppercase">Medicine</TableHead>
                    <TableHead className="text-[10px] font-bold uppercase text-center">Qty</TableHead>
                    <TableHead className="text-[10px] font-bold uppercase text-right">MRP</TableHead>
                    <TableHead className="text-[10px] font-bold uppercase text-right">Unit Price</TableHead>
                    <TableHead className="text-[10px] font-bold uppercase text-right">Tax</TableHead>
                    <TableHead className="text-[10px] font-bold uppercase text-right">Discount</TableHead>
                    <TableHead className="text-[10px] font-bold uppercase text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(drillOrder.medicines || []).map((m, i) => (
                    <TableRow key={i} className="hover:bg-slate-50">
                      <TableCell className="text-xs">
                        <div className="font-medium text-slate-800">{m.name}</div>
                        {m.composition && <div className="text-[10px] text-slate-400">{m.composition}</div>}
                        {m.batchNumber && <div className="text-[10px] text-slate-400">Batch: {m.batchNumber}</div>}
                      </TableCell>
                      <TableCell className="text-xs text-center tabular-nums">{m.quantity}</TableCell>
                      <TableCell className="text-xs text-right tabular-nums">{formatCurrency(m.mrp)}</TableCell>
                      <TableCell className="text-xs text-right tabular-nums">{formatCurrency(m.unitPrice)}</TableCell>
                      <TableCell className="text-xs text-right tabular-nums">{formatCurrency(m.tax)}</TableCell>
                      <TableCell className="text-xs text-right tabular-nums">{formatCurrency(m.discount)}</TableCell>
                      <TableCell className="text-xs text-right tabular-nums font-semibold">{formatCurrency(m.total)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 text-xs">
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-[10px] text-slate-500 uppercase font-semibold mb-1">Created By</p>
              <p className="font-medium text-slate-700">{drillOrder.createdBy}</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-[10px] text-slate-500 uppercase font-semibold mb-1">Date & Time</p>
              <p className="font-medium text-slate-700">{formatDateIST(drillOrder.createdAt)} {formatTimeIST(drillOrder.createdAt)}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
